import { useTranslation } from '@ice-web-app/shared-hooks';
import { Box } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IoAlert } from 'react-icons/io5';
import { DsButton } from '../buttons/DsButton';
import { DsIcon, DsIconProps } from '../icons/DsIcon';
import { DsTypography } from '../typography/DsTypography';
import { DsModal } from './DsModal';
import { DsAlertModalProps } from './types';

export function DsAlertModal({
  icon,
  title,
  subTitle,
  iconColor,
  hasFooter = true,
  type = 'warning',
  hasCloseIcon = false,
  approveButtonLoading = false,
  rejectButtonLoading = false,
  approveButtonColor = 'primary',
  rejectButtonColor = 'primary',
  approveButtonVariant = 'contained',
  rejectButtonVariant = 'outlined',
  open = false,
  content,
  buttonSize = 'large',
  rejectButtonDisabled = false,
  approveButtonDisabled = false,
  customFooter,
  onApprove,
  onReject,
  ...props
}: DsAlertModalProps) {
  const { t } = useTranslation();

  const iconColors = {
    primary: 'var(--mui-palette-primary-main)',
    error: 'var(--mui-palette-error-main)',
    warning: 'var(--mui-palette-warning-main)',
    success: 'var(--mui-palette-success-main)',
  };

  const getIcon = (): { customIcon?: React.ReactNode; icon?: DsIconProps['icon']; bgColor: string } => {
    if (icon) {
      return { customIcon: icon, bgColor: iconColors.primary };
    }

    switch (type) {
      case 'danger':
        return { customIcon: <IoAlert />, bgColor: iconColors.error };
      case 'success':
        return { icon: 'CHECK', bgColor: iconColors.success };
      case 'warning':
        return { customIcon: <IoAlert />, bgColor: iconColors.warning };
      case 'info':
        return { customIcon: <IoAlert />, bgColor: iconColors.primary };
      default:
        return { customIcon: <IoAlert />, bgColor: iconColors.primary };
    }
  };

  return (
    <DsModal open={open} {...props}>
      <DialogContent className="flex flex-col items-center">
        {hasCloseIcon && (
          <Box
            component="div"
            width="100%"
            className="flex p-4 justify-end items-center"
            sx={{ position: 'absolute', top: 0, right: 0 }}
          >
            {props.onClose && (
              <DsButton aria-label="close" size="xSmall" circle onClick={props.onClose} justIcon>
                <DsIcon size="lg" color="secondary.main" icon="CLOSE" />
              </DsButton>
            )}
          </Box>
        )}
        <Box
          color="white"
          sx={{
            width: '70px',
            height: '70px',
            borderRadius: '100%',
            bgcolor: iconColor ? iconColors[iconColor] : getIcon().bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DsIcon size={40} icon={getIcon().icon} customIcon={getIcon().customIcon} sx={{ color: 'white' }} />
        </Box>

        {content || (
          <Box>
            {title && typeof title === 'string' ? (
              <DsTypography variant="h5" my={5} textAlign="center">
                {title}
              </DsTypography>
            ) : (
              title
            )}
            {subTitle && typeof subTitle === 'string' ? (
              <DsTypography variant="body1" textAlign="center" color="text.secondary">
                {subTitle}
              </DsTypography>
            ) : (
              subTitle
            )}
          </Box>
        )}
      </DialogContent>

      {hasFooter && (
        <DialogActions>
          {customFooter}
          {onReject && (
            <DsButton
              onClick={() => onReject?.()}
              disabled={rejectButtonDisabled}
              variant={rejectButtonVariant}
              data-test="alert-modal-no-btn"
              color={rejectButtonColor}
              size={buttonSize}
              fullWidth
            >
              {props.rejectText || t('No')}
            </DsButton>
          )}
          {onApprove && (
            <DsButton
              loading={approveButtonLoading}
              disabled={approveButtonDisabled}
              data-test="alert-modal-yes-btn"
              onClick={() => onApprove?.()}
              variant={approveButtonVariant}
              color={approveButtonColor}
              size={buttonSize}
              fullWidth
            >
              {props.approveText || t('YES')}
            </DsButton>
          )}
        </DialogActions>
      )}
    </DsModal>
  );
}
